import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider';
import CompletionNotificationPopup from './CompletionNotificationPopup';
import PostRenewalForm from './PostRenewalForm';

/**
 * CompletionListener - Listens for ride/package completions and shows notifications
 * This component should be included in the main App layout
 */
const CompletionListener = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // State for completion popup
  const [completionPopup, setCompletionPopup] = useState({
    isOpen: false,
    postType: null,
    postId: null,
    postData: null
  });
  
  // State for renewal form
  const [renewalForm, setRenewalForm] = useState({
    isOpen: false,
    originalPost: null,
    postType: null
  });
  
  useEffect(() => {
    if (!user) return;
    
    // Subscribe to ride completions
    const rideCompletionSub = supabase
      .channel(`ride-completions-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'rides'
      }, async (payload) => {
        // Check if status changed to completed (case insensitive)
        if (payload.new.status === 'COMPLETED' || payload.new.status === 'completed') {
        console.log('Ride completion detected:', payload);
        
        // Check if this ride is associated with a post by the current user
        try {
          const { data: postData, error: postError } = await supabase
            .from('posts')
            .select('*')
            .eq('id', payload.new.post_id)
            .eq('user_id', user.id)
            .single();
            
          if (!postError && postData) {
            // Show completion popup for post author
            setCompletionPopup({
              isOpen: true,
              postType: 'ride',
              postId: postData.id,
              postData: postData
            });
          }
        } catch (error) {
          console.error('Error checking post ownership:', error);
        }
        }
      })
      .subscribe();
    
    // Subscribe to package completions (both completed and delivery_confirmed)
    const packageCompletionSub = supabase
      .channel(`package-completions-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'package_deliveries'
      }, async (payload) => {
        // Check if status changed to completed or delivery_confirmed (case insensitive)
        if (payload.new.status === 'completed' || payload.new.status === 'delivery_confirmed' || 
            payload.new.status === 'COMPLETED' || payload.new.status === 'DELIVERY_CONFIRMED') {
          console.log('Package completion detected:', payload);
          
          // Check if this package belongs to the current user
          try {
            const { data: packageData, error: packageError } = await supabase
              .from('packages')
              .select('*')
              .eq('id', payload.new.package_id)
              .eq('user_id', user.id)
              .single();
              
            if (!packageError && packageData) {
              // Show completion popup for package sender
              setCompletionPopup({
                isOpen: true,
                postType: 'package',
                postId: packageData.id,
                postData: packageData
              });
            }
          } catch (error) {
            console.error('Error checking package ownership:', error);
          }
        }
      })
      .subscribe();
    
    // DISABLED: This was causing duplicate popups when archiving posts
    // The ride completion subscription above is sufficient
    /*
    // Subscribe to direct post status changes (for testing or manual updates)
    const postStatusSub = supabase
      .channel(`post-status-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'posts',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        if (payload.new.status === 'completed' && payload.old.status !== 'completed') {
          console.log('Post marked as completed:', payload);
          
          setCompletionPopup({
            isOpen: true,
            postType: 'ride',
            postId: payload.new.id,
            postData: payload.new
          });
        }
      })
      .subscribe();
    */
    
    // DISABLED: This was causing duplicate popups when archiving packages
    // The package delivery completion subscription above is sufficient
    /*
    // Subscribe to direct package status changes
    const packageStatusSub = supabase
      .channel(`package-status-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'packages',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        if (payload.new.status === 'completed' && payload.old.status !== 'completed') {
          console.log('Package marked as completed:', payload);
          
          setCompletionPopup({
            isOpen: true,
            postType: 'package',
            postId: payload.new.id,
            postData: payload.new
          });
        }
      })
      .subscribe();
    */
    
    // Cleanup
    return () => {
      supabase.removeChannel(rideCompletionSub);
      supabase.removeChannel(packageCompletionSub);
      // Disabled subscriptions - no need to remove
      // supabase.removeChannel(postStatusSub);
      // supabase.removeChannel(packageStatusSub);
    };
  }, [user]);
  
  // Handle renewal request
  const handleRenew = (postId, postData) => {
    setRenewalForm({
      isOpen: true,
      originalPost: postData,
      postType: completionPopup.postType
    });
  };
  
  // Handle successful renewal
  const handleRenewalSuccess = (newPostId) => {
    // Navigate to the appropriate publish page to view the new post
    if (renewalForm.postType === 'ride') {
      navigate('/PublishPost');
    } else {
      navigate('/SendPackage');
    }
  };
  
  return (
    <>
      {/* Completion Notification Popup */}
      <CompletionNotificationPopup
        isOpen={completionPopup.isOpen}
        onClose={() => setCompletionPopup({ ...completionPopup, isOpen: false })}
        postType={completionPopup.postType}
        postId={completionPopup.postId}
        postData={completionPopup.postData}
        onRenew={handleRenew}
      />
      
      {/* Post Renewal Form */}
      <PostRenewalForm
        isOpen={renewalForm.isOpen}
        onClose={() => setRenewalForm({ ...renewalForm, isOpen: false })}
        originalPost={renewalForm.originalPost}
        postType={renewalForm.postType}
        onSuccess={handleRenewalSuccess}
      />
    </>
  );
};

export default CompletionListener;